/**
 * Derive a stable, collision-resistant 32-bit Agora UID from a user id.
 *
 * Agora RTC UIDs must be unsigned 32-bit integers and must match between the
 * token (minted by the backend) and the client that joins. Deriving the UID
 * deterministically from the user id means:
 *   - the same user always gets the same UID (stable across reconnects), and
 *   - two different users are extremely unlikely to collide (~1 in 4 billion),
 * unlike the previous "last 8 digits + timestamp" scheme which was neither
 * stable nor collision-safe.
 *
 * Uses FNV-1a (32-bit), clamped to 1..(2^32 - 1) since 0 lets Agora assign a
 * random UID, which we explicitly do not want.
 */
export const deriveAgoraUid = (userId: string | number): number => {
  const input = String(userId);
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply, kept within 32 bits.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // Ensure non-zero (0 means "let Agora choose").
  return hash === 0 ? 1 : hash;
};
